import {
  useCreateCallbackMutation,
  useUpdateCallbackMutation,
} from '../store/modules/merchant/api';
import { CallbackResponse, CallbackType } from '../store/modules/merchant/types';
import { isServerError } from '../util/helpers';

export type CallbackToUpsert = {
  callbackType: CallbackType;
  url: string;
  callbackId?: string;
  headers?: Record<string, string>;
} & Partial<CallbackResponse>;

/**
 * This hook can be used to update a callback, or create it if it doesn't exist.
 *
 * For the moment, we won't allow updates to inherited callbacks as this is a not a use
 * case that merchants will need, and it will lead to some side effects that are not yet
 * handled.
 */
const useCallbackUpsert = (merchantId: string) => {
  const [createCallback, { isLoading: isCreating }] = useCreateCallbackMutation();
  const [updateCallback, { isLoading: isUpdating }] = useUpdateCallbackMutation();

  const upsertOneCallback = async (callback: CallbackToUpsert) => {
    const { callbackType, url, callbackId, headers } = callback;
    const isInherited = callbackId && (callback.merchantId !== merchantId || callback.isInherited);
    if (isInherited) {
      throw new Error('The callback is inherited from the parent merchant and cannot be updated.');
    }

    if (callbackId) {
      await updateCallback({
        callbackId,
        merchantId,
        url,
        headers,
      }).unwrap();
    } else {
      await createCallback({
        merchantId,
        callbackType,
        url,
        headers,
      }).unwrap();
    }
  };

  const onCallbackUpsert = async (callback: CallbackToUpsert) => {
    try {
      await upsertOneCallback(callback);
    } catch (err) {
      if (isServerError(err) && err.data.statusCode === 400) {
        throw new Error(err.data.message);
      }
      throw err;
    }
  };

  const onCallbackUpsertBatch = async (callbacks: CallbackToUpsert[]) => {
    for (const callback of callbacks) {
      await onCallbackUpsert(callback);
    }
  };

  return {
    onCallbackUpsert,
    onCallbackUpsertBatch,
    isLoading: isCreating || isUpdating,
  };
};

export { useCallbackUpsert };
